import styles from '../Columns/columns.module.scss';
import { RESIZABLE_COLUMNS } from './resizableConfig';

type ColumnWidths = Record<string, number>;

/**
 * Get the CSS variable name for a column key
 * e.g., 'title' -> 'column_title_width'
 */
const getColumnCssKey = (columnKey: string): string => {
  return `column_${columnKey}_width`;
};

/**
 * Calculate base table width (all NON-resizable column widths).
 * Resizable columns are excluded because they're calculated separately
 * in getResizableColumnsWidth().
 */
const resizableColumnCssKeys = new Set(
  RESIZABLE_COLUMNS.map((col) => getColumnCssKey(col.key))
);

const getBaseTableWidth = (): number => {
  return Object.entries(styles).reduce((sum, [key, value]) => {
    if (!key.startsWith('column_') || !key.endsWith('_width')) return sum;
    if (resizableColumnCssKeys.has(key)) return sum;
    return sum + Number(value);
  }, 0);
};

/**
 * Calculate total width of resizable columns based on current widths.
 * Skips columns marked agentEntryOnly when canAgentEntry is false.
 */
const getResizableColumnsWidth = (
  columnWidths: ColumnWidths,
  canAgentEntry: boolean
): number => {
  return RESIZABLE_COLUMNS.reduce((sum, col) => {
    if (col.agentEntryOnly && !canAgentEntry) return sum;
    const width = columnWidths[col.key] ?? col.defaultWidth;
    return sum + width;
  }, 0);
};

/**
 * Calculate total table width
 * Base width + resizable columns width
 */
export const getTotalTableWidth = (
  columnWidths: ColumnWidths,
  canAgentEntry: boolean = true
): number => {
  const baseWidth = getBaseTableWidth();
  const resizableWidth = getResizableColumnsWidth(columnWidths, canAgentEntry);
  return baseWidth + resizableWidth;
};

/**
 * Get width for a specific column.
 * Returns stored width or default width for resizable columns.
 * Returns undefined for non-resizable columns.
 */
export const getColumnWidth = (
  columnKey: string,
  columnWidths: ColumnWidths
): number | undefined => {
  const resizableColumn = RESIZABLE_COLUMNS.find(
    (col) => col.key === columnKey
  );
  if (!resizableColumn) return undefined;

  return columnWidths[columnKey] ?? resizableColumn.defaultWidth;
};
